import { useState } from 'react';
import { Workflow, Plus, Play, Pause, Settings, BarChart3 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useCollectionWorkflows } from '@/hooks/useCollectionWorkflows';
import { PageHeader } from '@/components/PageHeader';

export default function CollectionWorkflows() {
  const { workflows, isLoading } = useCollectionWorkflows();

  const stats = [
    {
      title: "Active Workflows",
      value: workflows.filter(w => w.is_active).length,
      description: "Currently running",
      icon: Play,
    },
    {
      title: "Automated",
      value: workflows.filter(w => w.workflow_type === 'automatic').length,
      description: "Fully automated workflows",
      icon: Workflow,
    },
    {
      title: "Manual",
      value: workflows.filter(w => w.workflow_type === 'manual').length,
      description: "Manual intervention required",
      icon: Settings,
    },
    {
      title: "Success Rate",
      value: "89.3%",
      description: "Avg collection success",
      icon: BarChart3,
    }
  ];

  const getWorkflowTypeColor = (type: string) => {
    switch (type) {
      case 'automatic': return 'default';
      case 'manual': return 'secondary';
      case 'hybrid': return 'outline';
      default: return 'secondary';
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Collection Workflows"
        description="Manage automated collection processes and workflows"
        action={
          <Button className="gap-2">
            <Plus className="h-4 w-4" />
            New Workflow
          </Button>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <Card key={index}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {stat.title}
              </CardTitle>
              <stat.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
              <p className="text-xs text-muted-foreground">
                {stat.description}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Workflows Table */}
      <Card>
        <CardHeader>
          <CardTitle>Collection Workflows</CardTitle>
          <CardDescription>
            Configure automated collection processes for overdue bills
          </CardDescription>
        </CardHeader>
        <CardContent>
          {workflows.length === 0 ? (
            <div className="text-center py-12">
              <Workflow className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No workflows configured</h3>
              <p className="text-muted-foreground mb-4">
                Create your first collection workflow to automate bill collection processes
              </p>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create First Workflow
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Steps</TableHead>
                  <TableHead>Last Modified</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {workflows.map((workflow) => (
                  <TableRow key={workflow.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{workflow.name}</p>
                        {workflow.description && (
                          <p className="text-sm text-muted-foreground">
                            {workflow.description}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getWorkflowTypeColor(workflow.workflow_type)}>
                        {workflow.workflow_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={workflow.is_active ? "default" : "secondary"}>
                        {workflow.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {Array.isArray(workflow.steps) ? workflow.steps.length : 0} steps
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(workflow.updated_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            // Toggle workflow status
                          }}
                        >
                          {workflow.is_active ? (
                            <Pause className="h-4 w-4" />
                          ) : (
                            <Play className="h-4 w-4" />
                          )}
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Settings className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}